import { Alert, AlertTitle, Box, Button, IconButton } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import useAuth from '../hooks/useAuth';
import { remoteConfigData } from '../config';
import { isEmpty } from 'lodash';

interface IProps {
  marginTop?: number
  marginBottom?: number
  marginX?: number
}

type SurveyData = {
  code: string;
  userId: string;
  takeSurveyTapped: boolean;
  feedbackAlertDismissed: boolean;
  lastDismissed?: number;
};

const saveFeedback = (data:SurveyData) => {
  if (data.feedbackAlertDismissed)
    data.lastDismissed = Date.now();
  localStorage.setItem(`${data.code}-${data.userId}`, JSON.stringify(data));
};

const getFeedback = (userId:string, surveyCode:string): SurveyData | undefined=> {
  try {
    const dataString = localStorage.getItem(`${surveyCode}-${userId}`);
    if (dataString) {
      return JSON.parse(dataString);
    }
  } catch (e) {
    console.error('failed to read survey-data', e);
    return undefined;
  }
};

export const UserFeedbackAlert = ({ marginTop, marginBottom, marginX }: IProps) => {
  const enableFeedbackAlert = remoteConfigData.enableFeedbackAlert();
  if (!enableFeedbackAlert)
    return null;

  const { user } = useAuth();
  const feedbackSurveyCode = remoteConfigData.feedbackSurveyCode();
  const feedbackSurveyUrl = remoteConfigData.feedbackSurveyUrl();
  const feedbackSurveyTitle = remoteConfigData.feedbackSurveyTitle();
  const feedbackSurveyMessage = remoteConfigData.feedbackSurveyMessage();

  const feedbackData = useMemo(()=>getFeedback(user?.id, feedbackSurveyCode), []);

  const [showAlert, setShowAlert] = React.useState<boolean>(false);

  useEffect(() => {
    if (!user?.id)
      return ;

    if (isEmpty(feedbackData) || feedbackData?.userId !== user.id) {
      // We have a new user
      setShowAlert(true);
      return;
    }

    const dismissed24HoursAgo = feedbackData.lastDismissed && (Date.now() - feedbackData.lastDismissed) > 24 * 60 * 60 * 1000;
    if (feedbackData.feedbackAlertDismissed && dismissed24HoursAgo && !feedbackData.takeSurveyTapped) {
      // User has dismissed the alert
      setShowAlert(true);
      return;
    }

    if (feedbackData.takeSurveyTapped) {
      // Awesome! user has taken the survey
      setShowAlert(false);
      return;
    }
  }, [user]);

  const handleTakeSurvey = () => {
    saveFeedback({
      code: feedbackSurveyCode,
      userId: user.id,
      takeSurveyTapped: true,
      feedbackAlertDismissed: false
    });
    window.open(feedbackSurveyUrl, '_blank');
    setShowAlert(false);
  };

  const handleAlertClose = () => {
    saveFeedback({
      code: feedbackSurveyCode,
      userId: user.id,
      takeSurveyTapped: false,
      feedbackAlertDismissed: true
    });
    setShowAlert(false);
  };
  if (showAlert) {
    return  <Alert
          severity="info"
          sx={{
            mt: marginTop ?? 0,
            mb: marginBottom ?? 0,
            mx: marginX ?? 0,
            pt: 1,
            pb: 2
          }}
          action={
              <Box>
                  <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={handleTakeSurvey}
                  >
                      Take the Survey
                  </Button>
                  <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      sx={{
                        ml: 2
                      }}
                      onClick={handleAlertClose}
                  >
                      <CloseIcon/>
                  </IconButton>
              </Box>
          }
          onClose={handleAlertClose}
      >
          <AlertTitle>{feedbackSurveyTitle}</AlertTitle>
          {feedbackSurveyMessage}
      </Alert>;
  }

  return null;
};
